/* HomeStoriesStrip — TR-style horizontal carousel of latest ORIO Stories.
   Rendered in the Home page directly below the WelcomeHero to keep users
   engaged with fresh social content ("dopamine loop"). Muted video preview
   with an image poster fallback and a gradient+initial while media loads.

   Behavior:
   • Loads /api/stories/feed (national by default) — cached-friendly.
   • Renders up to 8 preview cards + a final "Vedi tutte" CTA card.
   • For image-type stories, `video_url` IS the image URL (server schema).
   • For video-type stories, uses `thumb_url` if present, otherwise renders
     a warm gradient with the author initial UNTIL the video actually loads
     enough to paint a frame — this avoids the "black square" bug reported
     on user-uploaded videos that lack a thumbnail.
   • Shows caption below author name (2-line truncate) when set.
   • Small music note icon top-left when the story has a soundtrack.
   • Tap → deep-link /app/match?tab=stories&story={id}
*/
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Sparkles, Music, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { Section } from "@/components/orio";

export default function HomeStoriesStrip() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [feed, sk] = await Promise.all([
          api.get("/stories/feed", { params: { scope: "national", limit: 8 } }),
          api.get("/stories/streak/me").catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setStories(Array.isArray(feed?.data?.stories) ? feed.data.stories : []);
        setStreak(sk?.data || null);
      } catch {
        /* silent */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Section eyebrow="Stories · ora" title="Le storie dei cani vicino a te" className="mt-6">
        <div className="-mx-4 sm:-mx-0 px-4 sm:px-0 flex gap-3 overflow-x-auto no-scrollbar pb-1">
          {[0,1,2,3].map((i) => (
            <div key={i} className="shrink-0 w-[168px] h-[260px] rounded-2xl bg-white border border-[#F0EBE3] animate-pulse" />
          ))}
        </div>
      </Section>
    );
  }

  if (!stories.length) return null;

  return (
    <Section
      eyebrow={
        <span className="inline-flex items-center gap-2">
          <span>Stories · ora</span>
          {streak && streak.days > 0 && (
            <StreakBadge days={streak.days} atRisk={streak.at_risk} activeToday={streak.active_today} />
          )}
        </span>
      }
      title="Le storie dei cani vicino a te"
      action={
        <Link
          to="/app/match?tab=stories"
          data-testid="home-stories-see-all-header"
          className="text-[13px] text-[#FF7A1A] font-medium"
        >
          Vedi tutte →
        </Link>
      }
      className="mt-6"
    >
      <div
        data-testid="home-stories-strip"
        className="-mx-4 sm:-mx-0 px-4 sm:px-0 flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
      >
        {stories.slice(0, 8).map((s, i) => (
          <StoryPreviewCard key={s.id || i} s={s} index={i} />
        ))}
        <SeeAllCard />
      </div>
    </Section>
  );
}

/* ─── Streak badge — small orange flame with day count ─── */
function StreakBadge({ days, atRisk, activeToday }) {
  const bg = atRisk ? "#F4B83C" : "#FF7A1A";
  const title = atRisk
    ? `Streak ${days} giorni — a rischio! Pubblica una storia oggi.`
    : activeToday
    ? `Streak ${days} giorni consecutivi 🔥`
    : `Streak ${days} giorni — inizia oggi la nuova pubblicazione.`;
  return (
    <span
      data-testid="stories-streak-badge"
      title={title}
      className="inline-flex items-center gap-1 pl-1 pr-2 py-[2px] rounded-full text-[10.5px] font-bold text-white tabular-nums"
      style={{
        background: `linear-gradient(135deg, ${bg} 0%, #F4B83C 100%)`,
        boxShadow: "0 4px 10px -3px rgba(255,122,26,0.55)",
      }}
    >
      <Flame className="h-3 w-3" fill="white" strokeWidth={2.2} />
      {days}
    </span>
  );
}

/* ─── Single preview card ─── */
function StoryPreviewCard({ s, index }) {
  const videoRef = useRef(null);
  const mediaType = s?.media_type || (s?.thumb_url ? "video" : "video");
  const isImage = mediaType === "image";
  const isVideo = mediaType === "video";
  // For image stories, `video_url` is the image URL.
  const posterUrl = s?.thumb_url || (isImage ? s?.video_url : null) || s?.photo_url;
  const videoUrl = isVideo ? s?.video_url : null;

  const authorName = s?.author?.name || s?.demo_author?.name || "ORIO";
  const authorPhoto = s?.author?.photo_url || s?.demo_author?.photo_url;
  const dogName = s?.dog?.name;
  const initial = (authorName || "?").trim().charAt(0).toUpperCase();
  const caption = (s?.caption || "").trim();
  const hasMusic = !!s?.music?.url || !!s?.music_track_id;

  // Video canPaint flag: true only after first frame is decoded, so before
  // that we show the gradient poster (never a black rectangle).
  const [videoReady, setVideoReady] = useState(!!posterUrl);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) return;
    v.muted = true;
    const onCanPlay = () => setVideoReady(true);
    v.addEventListener("loadeddata", onCanPlay);
    v.addEventListener("canplay", onCanPlay);
    const play = () => v.play().catch(() => {});
    const t = setTimeout(play, 200 + index * 60);
    return () => {
      clearTimeout(t);
      v.removeEventListener("loadeddata", onCanPlay);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, [isVideo, index]);

  // Warm gradient background shown behind the media while it loads AND
  // used as the poster for videos without a thumb_url — never black.
  const gradientBg = "linear-gradient(155deg, #FF7A1A 0%, #F4B83C 55%, #FFCB94 100%)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: index * 0.03, ease: "easeOut" }}
      className="snap-start shrink-0"
    >
      <Link
        to={`/app/match?tab=stories&story=${encodeURIComponent(s.id || "")}`}
        data-testid={`home-story-card-${s.id || index}`}
        className="block relative w-[168px] h-[260px] rounded-2xl overflow-hidden border border-[#F0EBE3] active:scale-[0.98] transition-transform"
        style={{
          background: gradientBg,
          boxShadow: "0 12px 28px -12px rgba(0,0,0,0.28), 0 4px 10px -4px rgba(0,0,0,0.08)",
        }}
      >
        {/* Background fallback: initial on gradient — always rendered so
            we NEVER show a black square while the video is loading. */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
             style={{
               opacity: videoReady && (isVideo || posterUrl) ? 0 : 1,
               transition: "opacity 200ms ease-out",
             }}>
          <span className="font-display text-6xl text-white/95 drop-shadow-lg">{initial}</span>
        </div>

        {/* Poster image (used for image stories or as video poster). Sits
            below the video so a broken video URL still shows the image. */}
        {posterUrl && (
          <img
            src={posterUrl}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}

        {/* Video (only for video stories) */}
        {isVideo && videoUrl && (
          <video
            ref={videoRef}
            src={videoUrl}
            poster={posterUrl || undefined}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: videoReady ? 1 : 0, transition: "opacity 250ms ease-out" }}
          />
        )}

        {/* Gradient overlay for legibility */}
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)" }} />

        {/* Play icon (muted video hint) */}
        {isVideo && (
          <div className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full bg-white/95 flex items-center justify-center"
               style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
            <Play className="h-3 w-3 text-black" fill="black" />
          </div>
        )}

        {/* Music note (top-left) */}
        {hasMusic && (
          <div className="absolute top-2.5 left-2.5 h-7 w-7 rounded-full bg-white/95 flex items-center justify-center"
               style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}
               title={s?.music?.title}>
            <Music className="h-3 w-3 text-[#FF7A1A]" />
          </div>
        )}

        {/* Author + dog + optional caption */}
        <div className="absolute inset-x-2.5 bottom-2.5">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-full overflow-hidden border-2 border-white shrink-0 bg-white flex items-center justify-center"
                  style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}>
              {authorPhoto ? (
                <img
                  src={authorPhoto} alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span className="text-[11px] font-bold text-[#FF7A1A]">{initial}</span>
              )}
            </span>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-white leading-tight truncate"
                   style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                {authorName}
              </div>
              {dogName && (
                <div className="text-[10px] text-white/95 leading-tight truncate"
                     style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                  con {dogName}
                </div>
              )}
            </div>
          </div>
          {caption && (
            <div
              className="mt-1.5 text-[11px] leading-[1.25] text-white font-medium"
              style={{
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                overflow: "hidden", textOverflow: "ellipsis",
                textShadow: "0 1px 4px rgba(0,0,0,0.75)",
              }}
              data-testid="home-story-caption"
            >
              {caption}
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

/* ─── Final "Vedi tutte" CTA card ─── */
function SeeAllCard() {
  return (
    <Link
      to="/app/match?tab=stories"
      data-testid="home-stories-see-all-cta"
      className="snap-start shrink-0 w-[168px] h-[260px] rounded-2xl overflow-hidden border relative flex flex-col items-center justify-center gap-3 active:scale-[0.98] transition-transform"
      style={{
        background: "linear-gradient(155deg, #FFF6E6 0%, #FFE0BE 55%, #FFCB94 100%)",
        borderColor: "#F0D9B0",
        boxShadow: "0 12px 28px -12px rgba(255,122,26,0.32), 0 4px 10px -4px rgba(0,0,0,0.08)",
      }}
    >
      <div className="h-16 w-16 rounded-full flex items-center justify-center"
           style={{
             background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
             boxShadow: "0 12px 26px -8px rgba(255,122,26,0.65)",
           }}>
        <ArrowRight className="h-7 w-7 text-white" strokeWidth={2.4} />
      </div>
      <div className="text-center px-3">
        <div className="text-[11px] uppercase tracking-[0.20em] text-[#C25612] font-bold inline-flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> ORIO Stories
        </div>
        <div className="text-[13px] font-semibold text-[#1A1A1A] mt-1 leading-tight">
          Scopri tutte<br />le storie
        </div>
      </div>
    </Link>
  );
}
